import React from 'react';
import { Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import PaywallGate from './components/PaywallGate';
import Landing from './pages/Landing';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import TotpChallenge from './pages/TotpChallenge';
import Dashboard from './pages/Dashboard';
import DealAnalyzer from './pages/DealAnalyzer';
import QualifyOpportunity from './pages/QualifyOpportunity';
import MyAnalyses from './pages/MyAnalyses';
import AnalysisDetail from './pages/AnalysisDetail';
import Leads from './pages/Leads';
import LeaseScenarios from './pages/LeaseScenarios';
import Improvements from './pages/Improvements';
import Account from './pages/Account';
import Support from './pages/Support';
import Help from './pages/Help';
import AdminDashboard from './pages/AdminDashboard';
import Marketplace from './pages/Marketplace';
import Onboarding from './pages/Onboarding';
import CompsManager from './pages/CompsManager';
import Pricing from './pages/Pricing';
import MockCheckout from './pages/MockCheckout';
import MockPortal from './pages/MockPortal';

// v1.1.0 — left-side navigation shell.
// Public routes render flat (no Layout). Protected routes wrap in
// <Layout>, which provides the persistent sidebar, top navbar,
// PaywallBanner, and DevSubscriptionPanel.
//
// Layout > PaywallGate ordering keeps the sidebar visible when a
// paywalled user lands on /deal-analyzer — they can still navigate
// to /pricing or /account to resolve their state.
export default function App() {
  // Public Access Lockdown (Waitlist-Only Mode): the dev-only mock Stripe
  // routes are registered ONLY in mock mode. In production REACT_APP_STRIPE_MOCK
  // is unset (CRA inlines it at build time), so these routes are never mounted
  // and the mock UI is unreachable — the backing /api/mock-stripe-* functions
  // also hard-return 403 in prod. Same convention as DevSubscriptionPanel.
  const isMock = process.env.REACT_APP_STRIPE_MOCK === 'true';

  // BL-017 Opportunity Qualification Engine — feature-flagged at the build
  // level (same convention as the mock routes). Unset in any environment that
  // hasn't enabled the feature, so the route is simply not mounted.
  const qualificationEnabled = process.env.REACT_APP_QUALIFICATION_ENABLED === 'true';

  return (
    <Routes>
      {/* ── Public — no Layout shell ─────────────────────────── */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/challenge" element={<TotpChallenge />} />
      <Route path="/pricing" element={<Pricing />} />

      {/* COE-001 — Controlled Onboarding Entry Experience. /onboarding is the
          PUBLIC entry page for the invited cohort: it explains Rentava, then
          routes to /login or the cohort-gated Google OAuth flow. It does NOT
          weaken Public Access Lockdown — there is no public account creation
          here, and every OAuth session is still gated at /auth/callback
          (lib/onboardingAllowlist, fail-closed). The prior protected
          placeholder stub at this path was retired. */}
      <Route path="/onboarding" element={<Onboarding />} />

      {/* Public Access Lockdown — this placeholder surface is NOT part of the
          approved Landing → Waitlist public path. It stays auth-gated (no
          public access) rather than deleted, so it can be restored later. */}
      <Route
        path="/marketplace"
        element={
          <ProtectedRoute>
            <Marketplace />
          </ProtectedRoute>
        }
      />

      {/* Mock Stripe — dev only; registered only when REACT_APP_STRIPE_MOCK=true
          so the mock UI is never publicly reachable in production. */}
      {isMock && (
        <>
          <Route path="/mock-checkout" element={<MockCheckout />} />
          <Route path="/mock-portal" element={<MockPortal />} />
        </>
      )}

      {/* ── Protected — wrapped in Layout ────────────────────── */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout><Dashboard /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/deal-analyzer"
        element={
          <ProtectedRoute>
            <Layout>
              <PaywallGate>
                <DealAnalyzer />
              </PaywallGate>
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* BL-017 — Opportunity Qualification triage. Available to ALL
          authenticated tiers (the Deal Analyzer remains the premium product),
          so NOT wrapped in PaywallGate. Feature-flag gated at mount. */}
      {qualificationEnabled && (
        <Route
          path="/qualify"
          element={
            <ProtectedRoute>
              <Layout><QualifyOpportunity /></Layout>
            </ProtectedRoute>
          }
        />
      )}
      <Route
        path="/my-analyses"
        element={
          <ProtectedRoute>
            <Layout><MyAnalyses /></Layout>
          </ProtectedRoute>
        }
      />
      {/* BL-048 (ADR-068) — stable analysis-detail route. Owner-scoped read;
          safe not-found for missing/foreign/malformed ids. */}
      <Route
        path="/my-analyses/:analysisId"
        element={
          <ProtectedRoute>
            <Layout><AnalysisDetail /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads"
        element={
          <ProtectedRoute>
            <Layout><Leads /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/:leadId/scenarios"
        element={
          <ProtectedRoute>
            <Layout><LeaseScenarios /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/:leadId/improvements"
        element={
          <ProtectedRoute>
            <Layout><Improvements /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <Layout><Account /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/comps"
        element={
          <ProtectedRoute>
            <Layout><CompsManager /></Layout>
          </ProtectedRoute>
        }
      />
      {/* BL-037 Story 37.1 — in-app Feedback & Support capture. Available to
          ALL authenticated tiers (the controlled-onboarding support loop),
          so NOT wrapped in PaywallGate. */}
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <Layout><Support /></Layout>
          </ProtectedRoute>
        }
      />
      {/* BL-037 Story 37.2 — static, self-service Help Center. All
          authenticated tiers; links to the /support surface (Story 37.1). */}
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <Layout><Help /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <Layout><AdminDashboard /></Layout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
